import * as Sentry from "@sentry/react";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { classifyLedgerEntryExpiration, ledgerEntryExpirationLabel } from "../../../../src/shared/ledgerEntryExpiration";
import Auth from "../../Auth";
import { SudoButton } from "../../sudokit/components/SudoButton";

const LEDGER_PAGE_SIZE = 200;

const formatTimestamp = (timestamp) => moment(timestamp).local().format("YYYY-MM-DD HH:mm:ss");

const billingStatusClasses = {
  charged: "bg-green-100 text-green-800 border border-green-200",
  no_charge: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  failed: "bg-red-100 text-red-800 border border-red-200",
  pending: "bg-gray-100 text-gray-700 border border-gray-200",
};

const renderBillingBadge = (billingContext) => {
  if (!billingContext) {
    return null;
  }

  const status = billingContext.status || "pending";
  if (status === "charged" || status === "pending" || status === "no_charge") {
    return null;
  }

  const className = `text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${billingStatusClasses[status] || billingStatusClasses.pending}`;
  let label = status.replace("_", " ");
  if (status === "failed") {
    label = "Auto-Refund";
  }
  const tooltipParts = [];
  if (billingContext.failureReason) {
    tooltipParts.push(`Failure: ${billingContext.failureReason}`);
  }
  if (typeof billingContext.hadOutput === "boolean") {
    tooltipParts.push(`Output: ${billingContext.hadOutput ? "yes" : "no"}`);
  }
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(" | ") : undefined;

  return (
    <span
      className={className}
      title={tooltip}
    >
      {label}
    </span>
  );
};

const expirationBadgeClasses = {
  expiring: "bg-amber-100 text-amber-800 border border-amber-200",
  non_expiring: "bg-blue-100 text-blue-800 border border-blue-200",
};

// Only credit-grant entries get a badge; consumption/cancelled rows would
// drown the list in "Not applicable" pills.
const renderExpirationBadge = (type) => {
  const className = expirationBadgeClasses[classifyLedgerEntryExpiration(type)];
  if (!className) {
    return null;
  }

  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${className}`}>{ledgerEntryExpirationLabel(type)}</span>;
};

const Ledger = ({ subscription }) => {
  const [selectedLedgerId, setSelectedLedgerId] = useState("trial");
  const [availableLedgers, setAvailableLedgers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [ledgerHasMore, setLedgerHasMore] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState();
  const [loadingEntryDetail, setLoadingEntryDetail] = useState(false);
  const [ledgerError, setLedgerError] = useState();
  const [actionError, setActionError] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [loadingMoreLedger, setLoadingMoreLedger] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const formRef = useRef();

  const fetchLedgerPage = useCallback(async (ledgerId, offset = 0) => {
    return await Auth.fetchWithIdToken(`/api/admin/ledger/${subscription.id}/${ledgerId}`, {
      limit: LEDGER_PAGE_SIZE,
      offset,
    });
  }, [subscription.id]);

  const loadAvailableLedgers = useCallback(async () => {
    setLoadingLedger(true);
    const result = await Auth.fetchWithIdToken(`/api/admin/ledger/${subscription.id}/availableLedgers`);
    const current = result.find((option) => option.current);
    setAvailableLedgers(result);
    if (result.length > 0) {
      setSelectedLedgerId(current ? current.value : result[0].value);
    }
    setLoadingLedger(false);
  }, [subscription.id]);

  const loadLedger = useCallback(async () => {
    setLoadingLedger(true);
    try {
      const result = await fetchLedgerPage(selectedLedgerId, 0);
      setLedger(result.entries ?? []);
      setLedgerTotal(result.total ?? 0);
      setLedgerBalance(result.balance ?? 0);
      setLedgerHasMore(Boolean(result.hasMore));
      setLedgerError(null);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { selectedLedgerId },
        extra: { context: "load_ledger" },
      });
      setLedgerError(err.message || "Failed to load ledger entries");
    } finally {
      setLoadingLedger(false);
    }
  }, [fetchLedgerPage, selectedLedgerId]);

  const loadMoreLedger = async () => {
    if (!ledgerHasMore || loadingMoreLedger) {
      return;
    }
    setLoadingMoreLedger(true);
    try {
      const result = await fetchLedgerPage(selectedLedgerId, ledger.length);
      setLedger((current) => [...current, ...(result.entries ?? [])]);
      setLedgerTotal(result.total ?? 0);
      setLedgerBalance(result.balance ?? 0);
      setLedgerHasMore(Boolean(result.hasMore));
      setLedgerError(null);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { selectedLedgerId },
        extra: { context: "load_more_ledger" },
      });
      setLedgerError(err.message || "Failed to load more ledger entries");
    } finally {
      setLoadingMoreLedger(false);
    }
  };

  const openLedgerEntry = async (entry) => {
    setSelectedEntry(entry);
    setLoadingEntryDetail(true);
    try {
      const fullEntry = await Auth.fetchWithIdToken(`/api/admin/ledger/${subscription.id}/entry/${entry.id}`);
      setSelectedEntry(fullEntry);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { entryId: entry.id },
        extra: { context: "load_ledger_entry_detail" },
      });
      setLedgerError(err.message || "Failed to load ledger entry details");
      setSelectedEntry(null);
    } finally {
      setLoadingEntryDetail(false);
    }
  };

  useEffect(() => {
    loadAvailableLedgers();
  }, [subscription.id, loadAvailableLedgers]);

  useEffect(() => {
    loadLedger();
  }, [subscription.id, selectedLedgerId, loadLedger]);

  const addLedgerEntry = async (payload) => {
    const result = await Auth.fetchWithIdToken(`/api/admin/ledger/${subscription.id}/add`, payload);
    if (!result.success) {
      console.log("Error result", result);
      throw new Error("Failed to add ledger entry");
    }
    return result;
  };

  const handleRefundEntry = async (entry) => {
    try {
      setLoading(true);
      const payload = {
        amount: -entry.amount, // Negative amount for refund
        expires: true, // Refunds are always expiring credits
        note: `Refund for ledger entry ${entry.id}`,
      };
      await addLedgerEntry(payload);
      await loadLedger();
      setSelectedEntry(null); // Close the modal after successful refund
      setActionError(null);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminAddEntry = async (e) => {
    e.preventDefault();
    const payload = {
      amount: e.target.amount.value,
      expires: e.target.expires.checked,
      note: e.target.note.value,
    };
    if (!payload.amount) {
      setActionError("Amount is required");
      return;
    }
    if (!payload.note) {
      setActionError("Note is required");
      return;
    }
    try {
      setLoading(true);
      await addLedgerEntry(payload);
      await loadLedger();
      formRef.current.reset();
      setActionError(null);
    } catch (err) {
      setActionError(err.message);
      return;
    } finally {
      setLoading(false);
    }
  };

  const exportLedger = async () => {
    try {
      setExportLoading(true);
      const blob = await Auth.fetchFileWithIdToken(`/api/export-ledger`, {
        subscriptionId: subscription.id,
        ledgerId: selectedLedgerId,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ledger_${selectedLedgerId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: "admin-ledger-export" } });
      setLedgerError(error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const balance = ledgerBalance;
  const hasLedger = ledgerTotal > 0;
  const showingPartialLedger = ledger.length < ledgerTotal;

  const currentAvailable = availableLedgers.find((option) => option.current);
  const currentNotSelected = currentAvailable && currentAvailable.value !== selectedLedgerId;
  const showCurrentButton = currentNotSelected && currentAvailable;
  let runningBalance = balance;

  return (
    <div className="p-2">
      <Modal
        isOpen={!!selectedEntry}
        style={{
          content: {
            textAlign: "left",
            padding: "0",
            border: "none",
            borderRadius: "18px",
            maxWidth: "900px",
            width: "92vw",
            margin: "auto",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          },
        }}
        onRequestClose={() => setSelectedEntry(null)}
      >
        {selectedEntry && (() => {
          if (loadingEntryDetail) {
            return (
              <div className="rounded-2xl shadow-2xl bg-white p-8 text-center text-sm text-gray-600">
                Loading entry details...
              </div>
            );
          }

          const invocationId =
            selectedEntry.invocationId ||
            selectedEntry.context?.billingContext?.invocation_id ||
            selectedEntry.context?.billingContext?.invocationId ||
            selectedEntry.context?.invocationId ||
            selectedEntry.context?.invocation_id ||
            null;

          return (
            <div className="rounded-2xl shadow-2xl bg-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 border-b border-gray-200">
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400">Ledger Entry</p>
                    <h2 className="text-2xl font-semibold text-gray-900">Entry Details</h2>
                  </div>
                  {renderBillingBadge(selectedEntry.billingContext || selectedEntry.context?.billingContext)}
                  {selectedEntry?.amount < 0 ? (
                    <SudoButton
                      onClick={(e) => {
                        e.preventDefault();
                        handleRefundEntry(selectedEntry);
                      }}
                      disabled={loading}
                      variant="danger"
                      size="small"
                    >
                      {loading ? "Processing..." : "Refund"}
                    </SudoButton>
                  ) : null}
                </div>
                <div className="text-sm text-gray-500 font-mono">{formatTimestamp(selectedEntry.createdAt)}</div>
              </div>

              <div className="p-6 space-y-5 text-base text-gray-900">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</p>
                    <p className="text-sm text-gray-900">{selectedEntry.type}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Source</p>
                    <div className="text-sm text-gray-900 break-words break-all">{selectedEntry.source || "None"}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Expiration</p>
                    <p className="text-sm text-gray-900">{ledgerEntryExpirationLabel(selectedEntry.type)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Balance After Entry</p>
                    <p className="text-sm text-gray-900">{selectedEntry.balanceAfter?.toLocaleString() || "—"}</p>
                  </div>
                  {invocationId && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Invocation</p>
                      <a
                        href={`/admin/invocation/${invocationId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-app-primary font-semibold break-all hover:underline text-sm"
                      >
                        {invocationId}
                      </a>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                    <p className={`text-lg font-bold ${selectedEntry.amount > 0 ? "text-green-600" : selectedEntry.amount < 0 ? "text-red-600" : "text-gray-800"}`}>
                      {selectedEntry.amount > 0 ? "+" : ""}
                      {selectedEntry.amount.toLocaleString()}
                    </p>
                  </div>
                </div>

                {selectedEntry.note && (
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Note</p>
                    <p className="text-base text-gray-800 whitespace-pre-wrap">{selectedEntry.note}</p>
                  </div>
                )}

                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Context</span>
                  <div className="bg-gray-100 text-gray-900 p-3 rounded-xl text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto border border-gray-200 shadow-inner">
                    <pre>{JSON.stringify(selectedEntry.context, null, 2)}</pre>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      <div className="space-y-4">
        {/* Controls Section */}
        <div className="rounded border border-gray-200">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              onChange={(e) => {
                e.preventDefault();
                setSelectedLedgerId(e.target.value);
              }}
              value={selectedLedgerId}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-app-primary-cta focus:border-app-primary-cta transition duration-150"
            >
              {availableLedgers.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label} {option.current && "(current)"}
                </option>
              ))}
            </select>

            <SudoButton
              onClick={(e) => {
                e.preventDefault();
                loadLedger();
              }}
              variant="primary"
              size="small"
            >
              Refresh
            </SudoButton>

            {hasLedger && (
              <SudoButton
                disabled={loadingLedger || exportLoading}
                onClick={exportLedger}
                variant="secondary"
                size="small"
                state={exportLoading ? "loading" : "default"}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </SudoButton>
            )}

            {showCurrentButton && (
              <SudoButton
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedLedgerId(currentAvailable.value);
                }}
                variant="tertiaryGrey"
                size="small"
              >
                Current
              </SudoButton>
            )}

            {/* Current Balance - Inline */}
          </div>
          {hasLedger && (
            <div className="ml-auto flex items-center gap-2 bg-app-primary-cta-transparent-15 px-3 py-1 rounded border">
              <span className="text-sm font-medium text-app-primary-cta">Current Quota:</span>
              <span className="text-lg font-bold text-app-primary-cta">{balance.toLocaleString()}</span>
              {showingPartialLedger ? (
                <span className="text-xs text-app-primary-cta">({ledger.length.toLocaleString()} of {ledgerTotal.toLocaleString()} entries loaded)</span>
              ) : null}
            </div>
          )}
        </div>

        {/* Ledger Entries Section */}
        <div className="rounded border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Ledger Entries</h3>

          <div className="">
            {ledgerError && (
              <div className="mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {ledgerError}
              </div>
            )}
            {!hasLedger && !loadingLedger && <div className="text-center py-6 text-gray-500 text-sm">No ledger entries found</div>}

            {loadingLedger && <div className="text-center py-6 text-gray-500 text-sm">Loading ledger...</div>}

            {hasLedger && (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {ledger.map((entry) => {
                  const timestampDisplay = formatTimestamp(entry.createdAt);
                  const postBalance = runningBalance;
                  runningBalance += -entry.amount;

                  return (
                    <button
                      type="button"
                      onClick={() => openLedgerEntry(entry)}
                      key={entry.id}
                      className="w-full text-left flex items-center gap-3 p-2 bg-gray-100 rounded hover:bg-gray-200 hover:drop-shadow-sm transition-colors border-none cursor-pointer"
                    >
                      <div className="text-xs text-gray-500 font-mono w-20 flex-shrink-0">{timestampDisplay}</div>

                      <div className="flex-1 text-xs text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{entry.largeText}</span>
                          {renderExpirationBadge(entry.type)}
                          {renderBillingBadge(entry.billingContext)}
                        </div>
                        {entry.smallText && <span className="text-gray-600">({entry.smallText})</span>}
                      </div>

                      <div
                        className={`text-sm font-semibold tabular-nums w-24 text-right ${entry.amount > 0 ? "text-green-600" : entry.amount < 0 ? "text-red-600" : "text-gray-800"}`}
                        title={`Running Balance: ${postBalance.toLocaleString()}`}
                      >
                        {entry.amount > 0 ? "+" : ""}
                        {entry.amount?.toLocaleString()}
                      </div>
                    </button>
                  );
                })}
                {ledgerHasMore ? (
                  <div className="pt-2">
                    <SudoButton
                      onClick={(e) => {
                        e.preventDefault();
                        loadMoreLedger();
                      }}
                      disabled={loadingMoreLedger}
                      variant="tertiaryGrey"
                      size="small"
                      state={loadingMoreLedger ? "loading" : "default"}
                    >
                      {loadingMoreLedger ? "Loading..." : `Load older entries (${(ledgerTotal - ledger.length).toLocaleString()} remaining)`}
                    </SudoButton>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Add Entry Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Add to Current Ledger</h3>
              <p className="text-xs text-gray-600">Adds credit/debit to the active ledger (may differ from the one selected above).</p>
            </div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1 self-start sm:self-auto">Active Ledger</div>
          </div>

          <form
            onSubmit={handleAdminAddEntry}
            ref={formRef}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label
                  htmlFor="amount"
                  className="text-xs font-medium text-gray-700 mb-1"
                >
                  Amount
                </label>
                <input
                  type="number"
                  name="amount"
                  placeholder="e.g. 500"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary"
                />
              </div>

              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-700 mb-1">Expiration</span>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="expires"
                    defaultChecked={true}
                    className="h-4 w-4 text-app-primary focus:ring-app-primary border-gray-300 rounded"
                  />
                  Expires at cycle end
                </label>
              </div>
            </div>

            <div>
              <label
                htmlFor="note"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                name="note"
                rows={3}
                placeholder="Explain why this adjustment is being made..."
                className="w-full px-3 py-2 border box-border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary"
              />
            </div>

            <div className="flex justify-end">
              <SudoButton
                className="min-w-[160px]"
                type="submit"
                disabled={loading}
                variant="primary"
                size="small"
                state={loading ? "loading" : "default"}
              >
                {loading ? "Adding..." : "Add Entry"}
              </SudoButton>
            </div>

            {actionError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{actionError}</div>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Ledger;
